import moment from "moment-timezone";
import { lazy } from "react";
import { checkAppVersion } from "../../utils/versionCheck";
import OfflineRoute from "../components/OfflineRoute";

export async function exportAuthToken() {
  let result = sessionStorage.getItem("user");
  if (result) {
    result = JSON.parse(result);
    return result.accessToken;
  }
  return null;
}
const isAboutToExpire = (exp) => {
  const renewBeforeMS = parseInt(process.env.REACT_APP_TOKEN_RENEW_BEFORE || "1800000"); //milliseconds

  if (!exp) {
    return false;
  }
  //adding renewBefore time to current time
  //sothat we can regenerate the new access token before it's get expired.
  return Date.now() + renewBeforeMS >= exp;
};

const isExpired = (exp) => {
  if (!exp) return true;
  return Date.now() >= exp;
};

const getExpirationDate = (token) => {
  if (!token) {
    return null;
  }

  try {
    const jwt = JSON.parse(atob(token.split(".")[1]));

    // multiply by 1000 to convert seconds into milliseconds
    return jwt && jwt.exp ? jwt.exp * 1000 : null;
  } catch {
    // A truncated or non-JWT token used to throw from here, which lands inside
    // getHeaders() and surfaces as a failure of whichever API call happened to
    // be in flight. Null reads as expired, so the caller refreshes or logs out.
    return null;
  }
};

/**
 * One refresh at a time.
 *
 * Near expiry, EVERY in-flight request independently called /user/token/fetch.
 * Six parallel calls on a screen load meant six racing refreshes, and the last
 * writer won — the others could install a token that had already been replaced.
 * Callers now share whichever refresh is already running.
 */
let refreshInFlight = null;

const getToken = async () => {
  let userInfo = sessionStorage.getItem("user");
  if (!userInfo) {
    return null;
  }
  userInfo = JSON.parse(userInfo);
  let token = userInfo.accessToken;
  let expTime = getExpirationDate(userInfo.accessToken);

  // Call refresh API ONLY when:
  // - token is NOT expired
  // - token is about to expire
  if (!isExpired(expTime) && isAboutToExpire(expTime)) {
    if (!refreshInFlight) {
      refreshInFlight = fetch(`${process.env.REACT_APP_API_URL}/user/token/fetch`, {
        method: "POST",
        headers: {
          sessionId: userInfo.sessionId,
          refreshToken: userInfo.refreshToken,
        },
      })
        .then((r) => r.json())
        .finally(() => {
          refreshInFlight = null;
        });
    }
    const updatedToken = await refreshInFlight;

    if (
      updatedToken &&
      updatedToken.data &&
      Array.isArray(updatedToken.data) &&
      updatedToken.data.length === 0
    ) {
      userInfo = sessionStorage.getItem("user");
      if (userInfo) {
        userInfo = JSON.parse(userInfo);
        token = userInfo.accessToken;
      }
    } else if (updatedToken && updatedToken.data && Array.isArray(updatedToken.data) === false) {
      setToken(updatedToken.data);
      token = updatedToken.data.accessToken;
    }
  }

  return token;
};

const getSessionId = (async) => {
  let userInfo = sessionStorage.getItem("user");

  if (!userInfo) {
    return null;
  }
  userInfo = JSON.parse(userInfo);
  return userInfo.sessionId;
};

const setToken = async (updatedToken) => {
  let userInfo = sessionStorage.getItem("user");

  if (!userInfo || !updatedToken) {
    return null;
  }
  userInfo = JSON.parse(userInfo);
  userInfo.accessToken = updatedToken.accessToken;
  userInfo.sessionId = updatedToken.sessionId;
  userInfo.refreshToken = updatedToken.refreshToken;
  // Keep the expire value from original login (it doesn't change on refresh)
  sessionStorage.setItem("user", JSON.stringify(userInfo));
};

// Check if token is about to expire (within renewBeforeMS time)
export const isTokenAboutToExpire = () => {
  let userInfo = sessionStorage.getItem("user");
  if (!userInfo) {
    return { isExpiring: false, timeRemaining: 0 };
  }

  userInfo = JSON.parse(userInfo);
  const expTime = getExpirationDate(userInfo.accessToken);
  if (!expTime) {
    return { isExpiring: false, timeRemaining: 0 };
  }

  const renewBeforeMS = parseInt(process.env.REACT_APP_TOKEN_RENEW_BEFORE || "1800000"); // Default 2 minutes
  const currentTime = Date.now();
  const timeUntilExpiry = expTime - currentTime;
  const timeUntilRenewThreshold = expTime - currentTime - renewBeforeMS;

  // Token is about to expire if we're within the renewBeforeMS threshold
  const isExpiring = timeUntilRenewThreshold <= 0 && timeUntilExpiry > 0;

  return {
    isExpiring,
    timeRemaining: Math.max(0, timeUntilExpiry),
  };
};

// Automatic token refresh function - refreshes token every 3.5 hours
export const refreshTokenAutomatically = async (logSessionExtension = false) => {
  let userInfo = sessionStorage.getItem("user");
  if (!userInfo) {
    return false;
  }

  try {
    userInfo = JSON.parse(userInfo);
    const body = logSessionExtension ? JSON.stringify({ sessionExtended: true }) : undefined;
    const updatedToken = await fetch(`${process.env.REACT_APP_API_URL}/user/token/fetch`, {
      method: "POST",
      headers: {
        sessionId: userInfo.sessionId,
        refreshToken: userInfo.refreshToken,
        "Content-Type": "application/json",
      },
      body: body,
    }).then((r) => r.json());

    if (updatedToken && updatedToken.data && Array.isArray(updatedToken.data) === false) {
      setToken(updatedToken.data);
      return true;
    }
    return false;
  } catch (error) {
    return false;
  }
};
export async function getHostToken() {
  let result = sessionStorage.getItem("hosttoken");
  if (result) {
    return result;
  }
  return null;
}

export function checkAWSLoginStatus(response) {
  if (response.status === 401) {
    let e = new Error(response.message || "Token Expired.");
    e.name = "tokenExpired";
    throw e;
  } else if (response.status === 199) {
    return response;
  } else if (response.status === 403) {
    let e = new Error(response.message);
    e.name = "accessDenied";
    throw e;
  } else if (response.status === 409) {
    let e = new Error(response.message);
    e.name = "duplicateLogin";
    throw e;
  } else if (response.status === 400) {
    return response;
  } else if (response.status === 201) {
    return response;
  } else if (response.status !== 200) {
    throw Error(response.message || "Error in connecting to Server.");
  }
  return response;
}

// OFFLINE-UX — this block. DU.md §33 group C.
/**
 * A write that cannot possibly reach the server.
 *
 * `navigator.onLine === false` is the trustworthy direction of that flag: true
 * can still mean a captive portal, but false means the browser has no network,
 * so the request is guaranteed to fail. Failing now, with a reason an agent can
 * read, beats a 60s timeout followed by three retries that cannot succeed.
 *
 * The name matters — callers already branch on `err.name`, and this is
 * deliberately NOT `tokenExpired`, so nothing logs the agent out over a dropped
 * wifi connection.
 */
export const isOffline = () => navigator.onLine === false;

export const offlineError = () => {
  const e = new Error("You're offline — this didn't reach the server. Try again when connected.");
  e.name = "offline";
  return e;
};
// end OFFLINE-UX

export function checkStatus(response) {
  if (response.status === 401) {
    let e = new Error(response.message || "Token Expired.");
    e.name = "tokenExpired";
    throw e;
  } else if (response.status === 199) {
    return response;
  } else if (response.status === 201) {
    return response;
  } else if (response.status === 403) {
    let e = new Error(response.message);
    e.name = "accessDenied";
    throw e;
  } else if (response.status === 409) {
    let e = new Error(response.message);
    e.name = "duplicateLogin";
    throw e;
  } else if (response.status === 400) {
    let e = new Error(response.message || "Parameter is Missing.");
    throw e;
  } else if (response.status === 404) {
    let e = new Error(response.data);
    throw e;
  } else if (response.status !== 200) {
    throw Error(response.message || "Error in connecting to Server.");
  }
  return response;
}

export async function exportTenant() {
  let result = sessionStorage.getItem("loggedIn");
  if (result) {
    result = JSON.parse(result);
    return result.tenant;
  }
  return null;
}

export async function getHeaders(
  additionalHeaders = {},
  defaultHeaders = ["authorization", "Content-Type"]
) {
  let headers = {};
  if (defaultHeaders.indexOf("authorization") !== -1) {
    headers["authorization"] = await getToken();
    headers["sessionId"] = await getSessionId();
  }
  if (defaultHeaders.indexOf("hosttoken") !== -1) {
    headers["hosttoken"] = await getHostToken();
  }
  if (defaultHeaders.indexOf("Content-Type") !== -1) {
    headers["Content-Type"] = "application/json";
  }
  headers = {
    ...headers,
    ...additionalHeaders,
  };

  if (await exportTenant()) {
    headers.tenant = await exportTenant();
  }
  return headers;
}

export async function checkUrl(url) {
  return fetch(url, {
    method: "GET",
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error("HTTP error, status = " + response.status);
      }
      return response.json();
    })
    .catch((error) => {
      return Promise.reject(error);
    });
}

export function isValidUrl(url) {
  var res = url.match(/www./gi);
  var pattern = null;
  if (res && res.length > 0) {
    if (res[0] === "www." || res[0] === "WWW.") {
      pattern = /^((ftp|http|https):\/\/)?www\.([A-z]+)\.([A-z]{2,})/;
    } else {
      pattern =
        /^(?:(?:https?|ftp):\/\/)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:\/\S*)?$/; // eslint-disable-line
    }
  } else {
    pattern =
      /^(?:(?:https?|ftp):\/\/)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:\/\S*)?$/; // eslint-disable-line
  }
  return pattern.test(url);
}

export async function logError(additionalHeaders, params) {
  return fetch(`${process.env.REACT_APP_API_URL}/log`, {
    method: "POST",
    headers: await getHeaders(additionalHeaders),
    body: JSON.stringify(params),
  })
    .then((response) => {
      return response.json();
    })
    .then(checkStatus)
    .catch((error) => {
      return Promise.reject(error);
    });
}
/**
 *
 * @param {*} permission permission name
 * @todo This method used to check for restricted permissions
 */
export async function checkPermission(permission) {
  let user = sessionStorage.getItem("user");
  if (!user) {
    return false;
  }
  user = JSON.parse(user);
  if (!user || user.userRoles === undefined) {
    return false;
  }
  let restrictedPermissions =
    user.userRoles.restrictedPermissions === undefined ? [] : user.userRoles.restrictedPermissions;
  return !restrictedPermissions.includes(permission);
}
export async function getRestrictedPermissions() {
  let user = sessionStorage.getItem("user");
  if (!user) {
    return [];
  }
  user = JSON.parse(user);
  if (!user || user.userRoles === undefined) {
    return [];
  }
  let restrictedPermissions =
    user.userRoles.restrictedPermissions === undefined ? [] : user.userRoles.restrictedPermissions;
  return restrictedPermissions;
}

// When our New Build is deployed some time some chunks failed to load, In those case reload will work
// Ref: https://raphael-leger.medium.com/react-webpack-chunkloaderror-loading-chunk-x-failed-ac385bd110e0#:~:text=You%20get%20this%20error%20when,the%20underlying%20issue%20at%20all.
export const lazyWithRetry = (importFn) =>
  lazy(async () => {
    // 1. Check version quietly before attempting import.
    //    `checkAppVersion` returns immediately when the browser is offline and
    //    swallows its own failures, so this can never stop a route loading.
    await checkAppVersion(true);

    const pending = localStorage.getItem("pending-version");
    if (pending && navigator.onLine !== false) {
      // If a background check found a new version, sync and reload immediately
      localStorage.setItem("app-version", pending);
      localStorage.removeItem("pending-version");
      localStorage.removeItem("pending-version-shown");

      window.location.reload();
      return new Promise(() => {});
    }

    // 2. Loop Protection: Keep this in SESSION storage
    // (Each tab should independently try once)
    const CHUNK_RETRY_KEY = "chunk-retry-attempted";
    const alreadyRetried = sessionStorage.getItem(CHUNK_RETRY_KEY) === "true";

    try {
      const module = await importFn();
      sessionStorage.removeItem(CHUNK_RETRY_KEY); // Reset on success
      return module;
    } catch (e) {
      const isChunkError =
        /chunk|dynamically imported/i.test(e?.message || "") || e?.name === "ChunkLoadError";

      // Offline: a reload cannot succeed. It hands the tab to the browser's own
      // error page, which is the blank screen this used to produce, and takes
      // the layout, the nav and whatever the agent had open with it.
      //
      // Resolving to a component instead keeps the shell intact and confines
      // the failure to the route's own area. OfflineRoute is in the main
      // bundle on purpose — a lazy chunk is precisely what cannot be fetched
      // here.
      if (isChunkError && navigator.onLine === false) {
        return { default: OfflineRoute };
      }

      if (isChunkError && !alreadyRetried) {
        sessionStorage.setItem(CHUNK_RETRY_KEY, "true");
        window.location.reload();
        return new Promise(() => {});
      }
      throw e; // Let the ErrorBoundary handle actual code/network crashes
    }
  });

export const isSupportingTagNotAllowed = (componentName, tagName) => {
  let userInfo = sessionStorage.getItem("user");
  if (!userInfo) {
    return false;
  }

  userInfo = JSON.parse(userInfo);
  if (!userInfo?.supportingTags) {
    return false;
  }

  let componentSupportingTags = userInfo.supportingTags[componentName]?.length
    ? userInfo.supportingTags[componentName]
    : [];
  return !componentSupportingTags.includes(tagName);
};

export const getTooltipForDisabled = (label) => {
  return label + ": This feature is currently disabled for your role, contact administrator";
};

export function getDateWithTenantTimezone(date) {
  let timezone = JSON.parse(sessionStorage.getItem("tenantConfig")).timeZone
    ? JSON.parse(sessionStorage.getItem("tenantConfig")).timeZone
    : moment.tz.guess();
  if (date) {
    // eslint-disable-next-line no-param-reassign
    date = moment(date).tz(timezone).format("MM/DD/YYYY LT");
  }
  return date;
}

// Returns the tenant's timezone string used for date formatting
export function getTenantTimezone() {
  const tenantConfig = sessionStorage.getItem("tenantConfig");
  if (tenantConfig) {
    const parsed = JSON.parse(tenantConfig);
    if (parsed.timeZone) return parsed.timeZone;
  }
  return moment.tz.guess();
}

export function getHeading(menus, location, pageHeading) {
  if (!menus || !location) {
    return pageHeading;
  }
  const currentMenu = menus.find((menu) => menu.url === location.pathname);
  return currentMenu ? currentMenu.title : pageHeading;
}

// This flag was added for ASU client initially, there a new field(Assigned_technician__c) was used instead of Client_Adviser__c
export function isAssignedTechnicianEnabled() {
  let tenantConfig = sessionStorage.getItem("tenantConfig");
  let tenantConfigParsed = tenantConfig ? JSON.parse(tenantConfig) : null;
  return tenantConfigParsed?.isAssignTechnicianFeature ?? false;
}

//This flag is used to show job queue notification icon in header
export function showJobQueueNotificationIcon() {
  let tenantConfig = sessionStorage.getItem("tenantConfig");
  let tenantConfigParsed = tenantConfig ? JSON.parse(tenantConfig) : null;
  return tenantConfigParsed?.showJobQueueNotificationIcon ?? false;
}

//This added for SCUHS client, they want the Asign ticket Feature in side View ticket also, Assign ticket is for admin user only, that we give in all Escalated Tickets outer actions
//But if we enable this setting, it will be visible inside View INbox Tickets -> View ticket as well, after taking ownership of ticket
export function enableAssignTicketInSideView() {
  let tenantConfig = sessionStorage.getItem("tenantConfig");
  let tenantConfigParsed = tenantConfig ? JSON.parse(tenantConfig) : null;
  return tenantConfigParsed?.enableAssignTicketInSideView ?? false;
}

export function enableChangeQueueFeatureInViewTicket() {
  let tenantConfig = sessionStorage.getItem("tenantConfig");
  let tenantConfigParsed = tenantConfig ? JSON.parse(tenantConfig) : null;
  return tenantConfigParsed?.enableChangeQueueFeature ?? false;
}

//This function can be used in all the service api calls, to avoid repeated code
export async function apiRequest(endpoint, method = "GET", data = null, component) {
  // OFFLINE-UX — two lines. DU.md §33 group C.
  if (isOffline()) return Promise.reject(offlineError());
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
      method,
      headers: await getHeaders({ component }),
      body: data ? JSON.stringify(data) : undefined,
    });

    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(`HTTP error ${response.status}: ${response.statusText} - ${errorBody}`);
    }

    return checkStatus(await response.json());
  } catch (error) {
    return Promise.reject(error);
  }
}

//This was initially done for SCUHS client to append the agent name in the comment and show the name with comment
export function showAgentNameInComments() {
  let tenantConfig = sessionStorage.getItem("tenantConfig");
  let tenantConfigParsed = tenantConfig ? JSON.parse(tenantConfig) : null;
  return tenantConfigParsed?.showAgentNameInComments ?? false;
}

//function to prepare the options to show as filter in the table columns
export function generateLookupsValuesForColumns(data) {
  let lookups = {};
  if (!data || data.length === 0) return lookups;

  Object.keys(data[0]).forEach((key) => {
    let uniqueValues = [...new Set(data.map((row) => row[key] || "__blank__"))]; // Extract unique values
    if (uniqueValues.length > 0 && uniqueValues.length <= 50) {
      // Limit options for performance

      lookups[key] = Object.fromEntries(
        uniqueValues.map((val) => {
          let key = val !== "__blank__" ? val : "__blank__";
          let value = val !== "__blank__" ? val : "Unassigned";
          return [key, value];
        })
      );
    }
  });
  return lookups;
}

export function customFilterSearch(selectedValues, rowData, column) {
  const cellValue = rowData[column];

  // Handle text input filters (string search)
  if (typeof selectedValues === "string") {
    if (!selectedValues) return true; // No filter applied
    if (cellValue === null || cellValue === undefined) return false;
    return String(cellValue).toLowerCase().includes(selectedValues.toLowerCase());
  }

  // Handle lookup filters (array-based, like checkboxes/dropdowns)
  if (!Array.isArray(selectedValues)) return true; // No filter applied
  if (!selectedValues.length) return true;

  const isBlank = !cellValue || cellValue === "";
  if (selectedValues.includes("__blank__") && isBlank) return true;

  return selectedValues.includes(cellValue);
}

//funciton to check user is logged in or, this flag is already managed when user is logged in.
export function isUserLoggedIn() {
  let userInfo = sessionStorage.getItem("user");
  let userInfoParsed = userInfo ? JSON.parse(userInfo) : null;
  return userInfoParsed?.isLoggedIn ?? false;
}

//return the tenant name defined in the tenantConfig
export function getTenantName() {
  let tenantConfig = sessionStorage.getItem("tenantConfig");
  let tenantConfigParsed = tenantConfig ? JSON.parse(tenantConfig) : null;
  return tenantConfigParsed?.instituteName ?? "";
}

//return the accountTypes from tenantConfig
export function getAccountTypes() {
  let tenantConfig = sessionStorage.getItem("tenantConfig");
  let tenantConfigParsed = tenantConfig ? JSON.parse(tenantConfig) : null;
  return tenantConfigParsed?.incontactDetails?.accountTypes || [];
}

//return the default accountType from tenantConfig
export function getDefaultAccountType() {
  let tenantConfig = sessionStorage.getItem("tenantConfig");
  let tenantConfigParsed = tenantConfig ? JSON.parse(tenantConfig) : null;
  return tenantConfigParsed?.incontactDetails?.defaultAccountType || [];
}

//return the mediumTypes from tenantConfig
export function getMediumTypes() {
  let tenantConfig = sessionStorage.getItem("tenantConfig");
  let tenantConfigParsed = tenantConfig ? JSON.parse(tenantConfig) : null;
  return tenantConfigParsed?.incontactDetails?.mediumTypes || [];
}

//return the default mediumTypes from tenantConfig
export function getDefaultMediumTypes() {
  let tenantConfig = sessionStorage.getItem("tenantConfig");
  let tenantConfigParsed = tenantConfig ? JSON.parse(tenantConfig) : null;
  return tenantConfigParsed?.incontactDetails?.defaultMediumTypes || [];
}

const ARTICLE_HREF = /\/articles\/Knowledge\/([^?#]+)/i;

const normalizeTitle = (t) =>
  (t || "")
    .replace(/\+/g, " ")
    .replace(/-/g, " ")
    .trim()
    .replace(/\/+$/, "")
    .replace(/\s+/g, " ")
    .toLowerCase();

const TAB_LABLE_MAX = 10;
const shortenLabel = (title = "") =>
  title.length > TAB_LABLE_MAX ? `${title.slice(0, TAB_LABLE_MAX)}...` : title;

export const createArticleLinkHandler = (addTab, tabs, goToTab) => (e) => {
  const link = e.target.closest("a");
  if (!link || !addTab) return;

  const match = (link.getAttribute("href") || "").match(ARTICLE_HREF);

  if (!match) return;

  e.preventDefault();

  let articleTitle = match[1];
  try {
    articleTitle = decodeURIComponent(match[1]);
  } catch (err) {
    // keep the raw value
  }

  const existingIndex = (tabs || []).findIndex(
    (t) =>
      t.component === "SalesforceArticleTab" &&
      normalizeTitle(t.articleTitle) === normalizeTitle(articleTitle)
  );
  if (existingIndex !== -1 && goToTab) {
    goToTab(existingIndex, e);
    return;
  }

  addTab({
    label: shortenLabel(articleTitle),
    component: "SalesforceArticleTab",
    articleTitle,
  });
};

export const openArticleInTab = (addTab, tabs, goToTab, articleTitle, extra = {}) => {
  if (!addTab || !articleTitle) return;

  const existingIndex = (tabs || []).findIndex(
    (t) =>
      t.component === "SalesforceArticleTab" &&
      normalizeTitle(t.articleTitle) === normalizeTitle(articleTitle)
  );
  if (existingIndex !== -1 && goToTab) {
    goToTab(existingIndex);
    return;
  }

  addTab({
    label: shortenLabel(articleTitle),
    component: "SalesforceArticleTab",
    articleTitle,
    ...extra,
  });
};
